/**
 * Codici invito: come l'agenzia regala un piano a un proprio cliente.
 *
 * Il codice si legge a voce al telefono, quindi l'alfabeto esclude 0/O e 1/I/L
 * e viene mostrato in un carattere a larghezza fissa: chi lo detta e chi lo
 * scrive devono vedere la stessa cosa.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import { Ticket, Plus, Copy, Trash2, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { adminApi, type InviteCode } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/utils';

const PIANI = [
  { id: 'solo', label: 'Solo' },
  { id: 'essenziale', label: 'Essenziale' },
  { id: 'pro', label: 'Pro' },
  { id: 'business', label: 'Business' },
];

export function InviteCodes() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin-invite-codes'], queryFn: () => adminApi.inviteCodes(), retry: false });

  const [plan, setPlan] = useState('essenziale');
  const [maxUses, setMaxUses] = useState(1);
  /** Vuoto = per sempre: è il caso dei clienti dell'agenzia. */
  const [mesi, setMesi] = useState('');
  const [nota, setNota] = useState('');
  const [copiato, setCopiato] = useState<string | null>(null);

  const crea = useMutation({
    mutationFn: () => adminApi.createInviteCode({
      plan,
      maxUses,
      months: mesi.trim() === '' ? null : Number(mesi),
      expiresInDays: 90,
      note: nota.trim() || undefined,
    }),
    onSuccess: (r) => {
      setNota('');
      qc.invalidateQueries({ queryKey: ['admin-invite-codes'] });
      void copia(r.code.code);
      toast.success('Codice creato e copiato');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Creazione non riuscita'),
  });

  const elimina = useMutation({
    mutationFn: (id: string) => adminApi.deleteInviteCode(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-invite-codes'] }),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Non riesco a ritirarlo'),
  });

  async function copia(code: string) {
    try {
      await navigator.clipboard.writeText(code);
      setCopiato(code);
      setTimeout(() => setCopiato((c) => (c === code ? null : c)), 1800);
    } catch {
      toast.error('Copia non riuscita: selezionalo a mano');
    }
  }

  const codici = q.data?.codes ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Ticket className="h-4 w-4 text-indigo-600" /> Codici invito
        </CardTitle>
        <CardDescription>
          Un codice dà un piano a un cliente senza fargli mettere la carta. Lo usa lui in
          registrazione, o dalla sua pagina Abbonamento.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* generatore */}
        <form
          className="rounded-xl border p-4 grid gap-3 sm:grid-cols-[1fr_auto_auto] items-end"
          onSubmit={(e) => { e.preventDefault(); crea.mutate(); }}
        >
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="ic-piano">Piano</Label>
              <select
                id="ic-piano" value={plan} onChange={(e) => setPlan(e.target.value)}
                className="w-full h-9 rounded-md border bg-background px-3 text-sm"
              >
                {PIANI.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ic-usi">Quante volte</Label>
              <Input
                id="ic-usi" type="number" min={1} max={500} value={maxUses}
                onChange={(e) => setMaxUses(Math.max(1, Number(e.target.value) || 1))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ic-mesi">Per quanti mesi</Label>
              <Input
                id="ic-mesi" type="number" min={1} max={120} value={mesi}
                placeholder="per sempre"
                onChange={(e) => setMesi(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1 sm:col-span-1">
            <Label htmlFor="ic-nota">Per chi</Label>
            <Input
              id="ic-nota" value={nota} onChange={(e) => setNota(e.target.value)}
              placeholder="Es. Studio Rossi"
            />
          </div>
          <Button type="submit" disabled={crea.isPending}>
            {crea.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Genera
          </Button>
        </form>

        <p className="text-xs text-muted-foreground -mt-2">
          Lasciando vuoto il numero di mesi il piano non scade mai: è il caso dei clienti
          dell'agenzia. Il codice smette di poter essere usato dopo 90 giorni.
        </p>

        {/* elenco */}
        {q.isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Carico…
          </div>
        )}

        {!q.isLoading && codici.length === 0 && (
          <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
            Nessun codice ancora. Generane uno qui sopra e leggilo al cliente.
          </div>
        )}

        <div className="space-y-2">
          {codici.map((c: InviteCode) => {
            const esaurito = c.uses >= c.max_uses;
            return (
              <div key={c.id} className={cn(
                'rounded-xl border p-3 flex items-center gap-3 flex-wrap',
                esaurito && 'bg-muted/40'
              )}>
                <button
                  type="button"
                  onClick={() => void copia(c.code)}
                  className="font-mono text-sm font-semibold tracking-wider hover:text-indigo-600 flex items-center gap-2"
                  title="Copia"
                >
                  {c.code}
                  {copiato === c.code
                    ? <Check className="h-3.5 w-3.5 text-emerald-600" />
                    : <Copy className="h-3.5 w-3.5 opacity-40" />}
                </button>

                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 shrink-0">
                  {PIANI.find((p) => p.id === c.plan)?.label ?? c.plan}
                </span>

                <span className="text-xs text-muted-foreground">
                  {c.uses}/{c.max_uses} usati · {c.months ? `${c.months} mesi` : 'senza scadenza'}
                  {c.expires_at && ` · valido fino al ${format(new Date(c.expires_at), 'd MMM', { locale: it })}`}
                </span>

                {c.note && <span className="text-xs text-muted-foreground italic">{c.note}</span>}

                {c.redeemed.length > 0 && (
                  <span className="text-xs text-emerald-700">
                    {c.redeemed.map((r) => r.email).join(', ')}
                  </span>
                )}

                {/* Un codice già usato non si ritira: cancellarlo non toglierebbe
                    il piano a chi l'ha usato, lascerebbe solo noi senza traccia. */}
                {c.uses === 0 && (
                  <button
                    type="button"
                    onClick={() => elimina.mutate(c.id)}
                    className="ml-auto text-muted-foreground hover:text-destructive shrink-0"
                    aria-label={`Ritira ${c.code}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
